import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface AIChatSession {
  result: string;
  reference: Array<{
    title: string;
    reference: string;
  }>;
  session: string;
  chat: {
    id: string;
    request: string;
    requestDate: string;
    response: string;
    responseDate: string;
    session: string;
    agent: string;
    references: Array<{
      title: string;
      reference: string;
    }>;
    createdAt: string;
    updatedAt: string;
    positiveFeedBack: string;
    positiveFeedBackDate: string;
    negativeFeedBack: string;
    negativeFeedBackDate: string;
  };
}

export interface AIChatMessagesData {
  page: string;
  total: string;
  records: string;
  rows: Array<{
    id: string;
    request: string;
    requestDate: string;
    response: string;
    responseDate: string;
    session: string;
    agent: string;
    references: Array<{
      title: string;
      reference: string;
    }>;
    createdAt: string;
    updatedAt: string;
    positiveFeedBack: string;
    positiveFeedBackDate: string;
    negativeFeedBack: string;
    negativeFeedBackDate: string;
  }>;
}

export interface RateChatMessageResponse {
  id: string;
  request: string;
  requestDate: string;
  response: string;
  responseDate: string;
  session: string;
  agent: string;
  references: Array<{
    title: string;
    reference: string;
  }>;
  createdAt: string;
  updatedAt: string;
  positiveFeedBack: string;
  positiveFeedBackDate: string;
  negativeFeedBack: string;
  negativeFeedBackDate: string;
}

export interface SendChatMessageResponse {
  result: string;
  reference: Array<{
    title: string;
    reference: string;
  }>;
  session: string;
  chat: {
    id: string;
    request: string;
    requestDate: string;
    response: string;
    responseDate: string;
    session: string;
    agent: string;
    references: Array<{
      title: string;
      reference: string;
    }>;
    createdAt: string;
    updatedAt: string;
    positiveFeedBack: string;
    positiveFeedBackDate: string;
    negativeFeedBack: string;
    negativeFeedBackDate: string;
  };
}

@Injectable({ providedIn: 'root' })
export class AIChatService {
  baseUrl: string = environment.API_SERVER;

  constructor(private http: HttpClient) {}

  getChatSession() {
    return this.http.get<AIChatSession>(`${this.baseUrl}/ai-chat/greetings`);
  }

  getChatMessages(sessionId: string) {
    return this.http.get<AIChatMessagesData>(
      `${this.baseUrl}/ai-chat/sessions/${sessionId}`
    );
  }

  getLatestChatSession() {
    return this.http
      .get<string>(`${this.baseUrl}/ai-chat/sessions`, {
        responseType: 'text' as 'json',
      })
      .pipe(
        map((sessionId) => {
          return sessionId;
        }),
        catchError((error) => {
          console.log(error, 'ERROR');
          return of('error');
        })
      );
  }

  rateChatMessage({
    sessionId,
    feedback,
    messageId,
    feedbackType,
  }: {
    sessionId: string;
    feedback: string;
    messageId: string;
    feedbackType: 'POSITIVE' | 'NEGATIVE';
  }) {
    const body = {
      feedback,
      chatMessage: messageId,
      feedbackType,
      session: sessionId,
    };
    return this.http.post<RateChatMessageResponse>(
      `${this.baseUrl}/ai-chat/messages/ratings`,
      body
    );
  }

  sendChatMessage({
    message,
    sessionId,
  }: {
    message: string;
    sessionId: string;
  }) {
    const body = {
      message,
      session: sessionId,
    };
    return this.http.post<SendChatMessageResponse>(
      `${this.baseUrl}/ai-chat/messages`,
      body
    );
  }
}
